import { Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { isCPFOrCNPJ } from 'brazilian-values';
import { useFormik, FormikProvider } from 'formik';
import parsePhoneNumberFromString, {
  isValidPhoneNumber,
  parsePhoneNumber,
} from 'libphonenumber-js';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';
import * as yup from 'yup';

import {
  Address,
  PhoneInputFormik,
  TaxIDInputFormik,
  TextField,
} from '@woovi/form';
import { useMutationCallbacks } from '@woovi/relay';
import { onlyNumbers, PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { ActionButton, Collapse } from '@woovi/ui';
import { registerYupValidations } from '@woovi/utils';

import type { PaymentActiveWaitingCustomerFillData_charge$key } from './__generated__/PaymentActiveWaitingCustomerFillData_charge.graphql.ts';
import type { ChargeAddCustomerMutation } from './mutations/__generated__/ChargeAddCustomerMutation.graphql.ts';
import { ChargeAddCustomer } from './mutations/ChargeAddCustomerMutation.ts';
import PaymentActiveRegisterInfo from './PaymentActiveRegisterInfo.tsx';
import PaymentLinkActiveCashbackFidelityBalance from '../active/paymentLinkActive/PaymentLinkActiveCashbackFidelityBalance.tsx';
import PaymentLinkActiveEarnCashback from '../active/paymentLinkActive/PaymentLinkActiveEarnCashback.tsx';
import PaymentLinkActiveEarnCashbackFidelity from '../active/paymentLinkActive/PaymentLinkActiveEarnCashbackFidelity.tsx';
import PaymentActivePixCobValue from '../active/pixCob/PaymentActivePixCobValue.tsx';
import PaymentLinkAdditionalInfo from '../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../paymentLink/PaymentLinkComment.tsx';
import PaymentLinkPaymentTerm from '../paymentLink/PaymentLinkPaymentTerm.tsx';
import PaymentLinkTransactionDetails from '../PaymentLinkTransactionDetails.tsx';

registerYupValidations();

type PaymentActiveWaitingCustomerFillDataProps = {
  charge: PaymentActiveWaitingCustomerFillData_charge$key;
};

type Values = {
  name: string;
  taxID: string;
  email: string;
  phone: string;
  address?: {
    zipcode: string;
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    complement: string;
  };
  paymentMethodSelected: string;
};

const PaymentActiveWaitingCustomerFillData = (
  props: PaymentActiveWaitingCustomerFillDataProps,
): ReactNode => {
  const { t } = useTranslation();
  const theme = useTheme();

  const charge = useFragment<PaymentActiveWaitingCustomerFillData_charge$key>(
    graphql`
      fragment PaymentActiveWaitingCustomerFillData_charge on Charge {
        id
        ...PaymentActivePixCobValue_charge
        ...PaymentLinkTransactionDetails_charge
        ...PaymentLinkAdditionalInfo_charge
        ...PaymentLinkPaymentTerm_charge
        ...PaymentLinkComment_charge
        ...PaymentLinkActiveEarnCashback_charge
        ...PaymentLinkActiveCashbackFidelityBalance_charge
        ...PaymentLinkActiveEarnCashbackFidelity_charge
        paymentMethodSelected
      }
    `,
    props.charge,
  );

  const [chargeCustomerFillData] =
    useMutationCallbacks<ChargeAddCustomerMutation>({
      name: 'ChargeAddCustomer',
      mutation: ChargeAddCustomer,
    });

  const getPhone = (phone: string) => {
    const phoneWithoutPrefix =
      parsePhoneNumberFromString(phone)?.formatNational();

    if (!phoneWithoutPrefix) {
      return {};
    }

    return { phone: parsePhoneNumber(phone, 'BR').number };
  };

  const formikbag = useFormik<Values>({
    initialValues: {
      name: '',
      taxID: '',
      email: '',
      phone: '',
      paymentMethodSelected: charge.paymentMethodSelected || '',
    },
    validationSchema: yup.object().shape({
      name: yup.string().required(t('Name is required')),
      taxID: yup
        .string()
        .test('isCPF', t('Customer TaxID is invalid'), (value) => {
          if (!value) {
            return true;
          }

          return isCPFOrCNPJ(value);
        })
        .required(t('TaxID is required')),
      email: yup
        .string()
        .email(t('Email must be a valid email'))
        .required(t('Email is required')),
      phone: yup
        .string()
        .required(t('Phone is required'))
        .test('phone', t('Phone is invalid'), (value) => {
          if (!value) {
            return false;
          }

          const sanitizedPhone = onlyNumbers(value);

          return isValidPhoneNumber(sanitizedPhone, 'BR');
        }),
      address: yup.object().when('paymentMethodSelected', {
        is: PAYMENT_METHOD_TYPE.BOLETO,
        then: yup.object().shape({
          zipcode: yup.string().required(t('Zipcode is required')),
          street: yup.string().required(t('Street is required')),
          number: yup.string().required(t('Number is required')),
          neighborhood: yup.string().required(t('Neighborhood is required')),
          city: yup.string().required(t('City is required')),
          state: yup.string().required(t('State is required')),
          complement: yup.string(),
        }),
        otherwise: yup.object().nullable(),
      }),
    }),
    validateOnMount: true,
    onSubmit: (values) => {
      const { name, email, phone, taxID, address } = values;

      const getAddress = (): Record<string, unknown> => {
        if (!address) {
          return {};
        }

        return {
          address,
        };
      };

      chargeCustomerFillData({
        variables: {
          input: {
            chargeId: charge.id,
            name,
            email,
            ...getPhone(phone),
            taxID,
            ...getAddress(),
          },
        },
      });
    },
  });

  const { handleSubmit, isValid, isSubmitting } = formikbag;

  const disabled = !isValid || isSubmitting;

  const getAddressFields = (): ReactNode => {
    const paymentMethodsRequiringAddress = [PAYMENT_METHOD_TYPE.BOLETO];

    if (
      !paymentMethodsRequiringAddress.includes(charge?.paymentMethodSelected)
    ) {
      return null;
    }

    return (
      <Collapse
        header={
          <Typography
            sx={{
              display: 'flex',
              gap: 0.5,
              fontWeight: theme.typography.fontWeightBold,
            }}
          >
            {t('Address')}
          </Typography>
        }
        initialExpanded
      >
        <Address prefix='address' showHeader={false} />
      </Collapse>
    );
  };

  return (
    <FormikProvider value={formikbag}>
      <Stack sx={{ gap: 2 }}>
        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Stack
            sx={{
              justifyContent: 'center',
            }}
          >
            <PaymentActivePixCobValue charge={charge} />
            <PaymentLinkComment charge={charge} />
          </Stack>
        </Stack>

        <PaymentActiveRegisterInfo />

        <PaymentLinkActiveEarnCashback charge={charge} />
        <PaymentLinkActiveCashbackFidelityBalance charge={charge} />
        <PaymentLinkActiveEarnCashbackFidelity charge={charge} />

        <Stack>
          <TextField name='name' label={t('Name')} placeholder={t('Name')} />
          <TaxIDInputFormik
            name='taxID'
            label={t('CPF or CNPJ')}
            placeholder={t('CPF or CNPJ')}
          />
          <TextField name='email' label={t('Email')} placeholder={t('Email')} />
          <PhoneInputFormik
            name='phone'
            label={t('Phone')}
            type='tel'
            defaultCountry='br'
            onlyCountries={['br']}
          />
          {getAddressFields()}
        </Stack>
        <ActionButton disabled={disabled} onClick={() => handleSubmit()}>
          {t('Pay')}
        </ActionButton>
        <Stack>
          <PaymentLinkPaymentTerm charge={charge} />
          <PaymentLinkTransactionDetails charge={charge} />
          <PaymentLinkAdditionalInfo charge={charge} />
        </Stack>
      </Stack>
    </FormikProvider>
  );
};

export default PaymentActiveWaitingCustomerFillData;
